/**
 * Academy program sections (institutional framework):
 *  - ValuePillars: the 4 operational value pillars (lead section)
 *  - OurApproach: institutional description + Traditional vs SAA Mastery Sprint + 12-week cycle calendar
 *                 + daily 4+1 schedule + curriculum-by-subject + mastery explainer
 *  - AcademyFit: why-choose differentiators + right-fit + enrollment essentials
 * Styled to match Academy.jsx (dark surfaces, gold labels, theme color TC).
 */
import React from 'react';
import {
  Calendar, Layers, Brain, BookOpen, Calculator, Microscope, Video,
  Clock, CheckCircle2, X, MapPin, Wallet, GraduationCap, Trophy, Target,
  ChevronRight, Users, Repeat, Dumbbell, ArrowRight
} from 'lucide-react';

const GOLD = '#C9A961';

const Label = ({ children }) => (
  <div className="flex items-center gap-4 mb-4">
    <div className="h-px w-16" style={{ backgroundColor: GOLD }} />
    <span className="text-[10px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>{children}</span>
  </div>
);

/* ============ 4 OPERATIONAL VALUE PILLARS (LEAD SECTION) ============ */
export const ValuePillars = ({ TC, RGB, BG, SURFACE }) => {
  const pillars = [
    { icon: Clock, num: '01', name: '3-Hour Daily Block', tag: 'Mon–Thu · Live + Socratic',
      body: 'Four days of focused live/Socratic instruction — virtual and in-person, 12:00–3:00 PM — engineered around elite training schedules. Fridays are reserved for labs, field trips, and review; any Friday work stays lighter while still covering the full curriculum.' },
    { icon: Layers, num: '02', name: 'Mastery-Based Progression', tag: 'Prove it, don’t sit it',
      body: 'Scholars advance by proving standard mastery through applied biomechanics and portfolio capstones — not passive seat time. Redo, iterate, and demonstrate real understanding before moving on.' },
    { icon: Dumbbell, num: '03', name: 'Friday Master Sprints', tag: 'The 4+1 model',
      body: 'Dedicated Fridays for 1-on-1 AI/Teacher Socratic tutoring, film study, capstone reviews, and local training-facility work — turning the fifth day into a personalized performance lab.' },
    { icon: Repeat, num: '04', name: 'Integrated 12-Week Cycles', tag: '3 cycles · 6 electives / yr',
      body: 'Three annual academic cycles, each containing two 6-week elective sprints (e.g., The Math of Basketball Vol. 1 + NIL Strategy) — a rigorous, repeatable rhythm that stacks skills across the year.' },
  ];
  return (
    <section className="relative py-28 px-6 lg:px-8" data-testid="value-pillars" style={{ backgroundColor: BG }}>
      <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(rgba(${RGB},0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(${RGB},0.05) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
      <div className="relative z-10 max-w-7xl mx-auto">
        <Label>The Operating Model</Label>
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tight mb-4">Four systems that give<br/><span className="text-gray-600">athletes their hours back.</span></h2>
        <p className="text-gray-500 text-sm max-w-xl mb-16">A mastery-aligned, guided independent study academy — built around how modern scholar-athletes actually train, compete, and learn.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.04]">
          {pillars.map((p) => (
            <div key={p.num} className="p-8 group" style={{ backgroundColor: SURFACE }} data-testid={`value-pillar-${p.num}`}>
              <div className="flex items-start justify-between mb-6">
                <div className="text-[9px] font-bold tracking-[0.25em] uppercase text-gray-700">{p.tag}</div>
              </div>
              <div className="text-[54px] font-black leading-none mb-3" style={{ color: `${TC}18` }}>{p.num}</div>
              <div className="w-10 h-10 flex items-center justify-center border border-white/10 mb-4 group-hover:border-white/30 transition-all"><p.icon className="w-5 h-5" style={{ color: TC }} /></div>
              <div className="text-xs font-black tracking-[0.15em] uppercase mb-2" style={{ color: TC }}>{p.name}</div>
              <p className="text-gray-400 text-sm leading-[1.75]">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============ OUR APPROACH ============ */
export const OurApproach = ({ TC, RGB, BG, SURFACE }) => {
  const subjects = [
    { icon: BookOpen, name: 'Humanities & ELA', body: 'Reading, writing, and history built around big questions, primary sources, and the stories of the game — in small live seminars.' },
    { icon: Calculator, name: 'Mathematics', body: 'Mastery-based math applied to real athletic problems: efficiency, floor geometry, probability, and performance modeling.' },
    { icon: Microscope, name: 'Science', body: 'Inquiry-driven science with hands-on investigations, real data, and biomechanics tied straight to the athlete’s body.' },
    { icon: Video, name: 'Electives & Enrichment', body: 'Six-week applied electives — Math of Basketball, NIL & Personal Branding, Social Media & Media Training — each with a certificate + badge.' },
  ];
  const schedule = [
    { t: '12:00', name: 'Pregame Check-In', body: 'Biometric verification & daily Game Plan briefing from the AI Head Coach.' },
    { t: '12:10', name: 'Station 1 — Live Direct Instruction', body: 'Small-group, teacher-led instruction on the day’s core subject.' },
    { t: '1:10', name: 'Station 2 — Applied Lab', body: 'Sports-analytics problem solving with the AI Socratic tutor and interactive worksheets.' },
    { t: '2:05', name: 'Station 3 — Collaborative Analysis', body: 'Project and portfolio work, peer collaboration, applied challenges.' },
    { t: '2:50', name: 'Post-Game Debrief', body: 'Exit ticket, per-standard mastery check, and coach debrief. (3-hour block, 12:00–3:00 PM)' },
  ];
  const trad = [
    'Grind 30 abstract y = mx + b textbook problems',
    'Slope taught in the abstract — disconnected from anything real',
    'Repetitive worksheet drills to "cover" the standard',
    'Homework: more of the same',
    'Cost: a full class period + homework block, every day',
  ];
  const saa = [
    'Chart your own free-throw arc as y = mx + b',
    'm = your improvement rate · b = your starting FT% — solve for sessions to reach 85%',
    'Master the standard by applying it to YOUR game',
    'Proven once, permanently, in a portfolio capstone',
    'Result: 3+ hours a day handed back for athletic development',
  ];
  return (
    <section className="relative py-28 px-6 lg:px-8" style={{ backgroundColor: SURFACE }} data-testid="our-approach">
      <div className="relative z-10 max-w-7xl mx-auto">
        <Label>Our Approach</Label>
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tight mb-6">Rigor that fits<br/><span className="text-gray-600">a champion’s schedule.</span></h2>
        <p className="text-gray-300 text-base md:text-lg max-w-3xl leading-relaxed mb-4">
          Scholar Athlete Academy combines customizable, project-based rigor with full California state standards —
          <span style={{ color: TC }}> giving elite athletes hours back every day while building a verifiable academic ledger.</span>
        </p>

        {/* Real-World Example: Traditional vs SAA Mastery Sprint */}
        <div className="mt-10 border-2" style={{ borderColor: `rgba(${RGB},0.35)` }} data-testid="real-world-example">
          <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-2" style={{ backgroundColor: `rgba(${RGB},0.08)` }}>
            <div className="flex items-center gap-2"><Target className="w-4 h-4" style={{ color: TC }} /><span className="text-xs font-black uppercase tracking-[0.2em]" style={{ color: TC }}>Real-World Example · Linear Equations (y = mx + b)</span></div>
            <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400 border border-emerald-500/40 px-2 py-1">Saves 3+ hrs / day</span>
          </div>
          <div className="grid md:grid-cols-2">
            <div className="p-7 border-t md:border-t-0 md:border-r border-white/[0.06]" data-testid="example-traditional">
              <div className="text-sm font-black text-gray-400 mb-5 flex items-center gap-2"><X className="w-4 h-4 text-gray-600" /> Traditional Classroom</div>
              <ul className="space-y-3">
                {trad.map((x, i) => (<li key={i} className="flex gap-2.5 text-sm text-gray-500 leading-relaxed"><span className="text-gray-700 mt-0.5">—</span>{x}</li>))}
              </ul>
            </div>
            <div className="p-7 border-t md:border-t-0 border-white/[0.06]" style={{ backgroundColor: `rgba(${RGB},0.04)` }} data-testid="example-saa">
              <div className="text-sm font-black mb-5 flex items-center gap-2" style={{ color: TC }}><CheckCircle2 className="w-4 h-4" /> SAA Mastery Sprint</div>
              <ul className="space-y-3">
                {saa.map((x, i) => (<li key={i} className="flex gap-2.5 text-sm text-gray-200 leading-relaxed"><CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: TC }} />{x}</li>))}
              </ul>
            </div>
          </div>
        </div>

        {/* 12-Week Cycle Calendar */}
        <div className="mt-24">
          <Label>The Annual Rhythm</Label>
          <h3 className="text-2xl md:text-4xl font-black tracking-tight mb-4">3 × 12-Week Project Cycles</h3>
          <p className="text-gray-500 text-sm max-w-xl mb-10">Each 12-week cycle contains two focused 6-week elective sprints — a repeatable structure that stacks mastery across the year.</p>
          <div className="grid md:grid-cols-3 gap-px bg-white/[0.04]">
            {[
              { c: 'Cycle 1', a: 'The Math of Basketball Vol. 1', b: 'NIL & Personal Branding' },
              { c: 'Cycle 2', a: 'Social Media & Media Training', b: 'The Math of Basketball Vol. 2' },
              { c: 'Cycle 3', a: 'Advanced Analytics Capstone', b: 'Recruiting & Portfolio Sprint' },
            ].map((cy, i) => (
              <div key={i} className="p-7" style={{ backgroundColor: BG }} data-testid={`cycle-${i}`}>
                <div className="text-xs font-black tracking-[0.2em] uppercase mb-4" style={{ color: TC }}>{cy.c} · 12 Weeks</div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-300"><span className="text-[10px] font-mono text-gray-600 w-14">Wk 1–6</span><span className="font-bold text-white">{cy.a}</span></div>
                  <div className="flex items-center gap-2 text-sm text-gray-300"><span className="text-[10px] font-mono text-gray-600 w-14">Wk 7–12</span><span className="font-bold text-white">{cy.b}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Daily 4+1 Schedule */}
        <div className="mt-24">
          <Label>A Day at the Academy · The 4+1 Model</Label>
          <h3 className="text-2xl md:text-4xl font-black tracking-tight mb-4">Mon–Thu: Core Station Rotations.</h3>
          <p className="text-gray-500 text-sm max-w-xl mb-10">A focused 3-hour block, 12:00–3:00 PM, predictable every day — then Friday becomes a personalized Master Sprint.</p>
          <div className="border border-white/[0.06]" data-testid="daily-schedule">
            {schedule.map((row, i) => (
              <div key={i} className="grid grid-cols-[64px_1fr] sm:grid-cols-[100px_1fr] gap-4 p-5 border-b border-white/[0.06] last:border-0 items-start">
                <div className="flex items-center gap-1.5 text-xs font-mono font-bold" style={{ color: TC }}><Clock className="w-3.5 h-3.5 opacity-60" />{row.t}</div>
                <div><div className="text-sm font-black text-white">{row.name}</div><div className="text-gray-500 text-xs mt-0.5 leading-relaxed">{row.body}</div></div>
              </div>
            ))}
          </div>
          <div className="flex items-start gap-3 p-6 border-2 mt-4" style={{ borderColor: `rgba(${RGB},0.35)`, backgroundColor: `rgba(${RGB},0.05)` }} data-testid="friday-master-sprint">
            <Dumbbell className="w-6 h-6 flex-shrink-0" style={{ color: GOLD }} />
            <div>
              <div className="text-sm font-black text-white">Friday — Master Sprint & Portfolio Submissions</div>
              <p className="text-gray-400 text-xs mt-1 leading-relaxed">1-on-1 AI/Teacher Socratic tutoring, film study, capstone reviews, portfolio submissions, and local training-facility work. Lighter assigned work — but the full curriculum is still covered.</p>
            </div>
          </div>
        </div>

        {/* Curriculum by subject */}
        <div className="mt-24">
          <Label>The Curriculum</Label>
          <h3 className="text-2xl md:text-4xl font-black tracking-tight mb-10">Real subjects, taught through the game.</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.04]">
            {subjects.map((s, i) => (
              <div key={i} className="p-7" style={{ backgroundColor: BG }} data-testid={`subject-card-${i}`}>
                <s.icon className="w-6 h-6 mb-5" style={{ color: GOLD }} />
                <div className="text-sm font-black text-white mb-2">{s.name}</div>
                <p className="text-gray-400 text-sm leading-[1.7]">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============ WHY CHOOSE / RIGHT FIT / ENROLLMENT ============ */
export const AcademyFit = ({ TC, RGB, BG, SURFACE, navigate }) => {
  const why = [
    { n: '1', name: 'Built for gifted minds', body: 'A flexible four-day schedule for serious training, competition, and travel — without falling behind.' },
    { n: '2', name: 'Live + AI instruction', body: 'Credentialed live teaching plus a daily AI Head Coach and Socratic tutor. Not passive modules.' },
    { n: '3', name: 'Sports-centric curriculum', body: 'Every subject taught through the game and real data, aligned to CA standards, with applied electives.' },
    { n: '4', name: 'Mastery + accountability', body: 'Biometric presence, reasoning-gated mastery, per-standard tracking, and official transcripts.' },
    { n: '5', name: 'A verifiable academic ledger', body: 'A shareable Scholar-Athlete Portfolio plus official transcripts — a verified record recruiters and schools can trust.' },
  ];
  const fit = [
    'Has a serious student-athlete who needs a flexible schedule for training, competition, and travel',
    'Wants live + AI-powered instruction with real accountability — not passive modules',
    'Values standards-aligned rigor taught through sports and real-world application',
    'Wants official transcripts, mastery tracking, and a clear college-bound path',
    'Wants a personalized, small-cohort program that adapts to each scholar’s pace',
  ];
  const notFit = [
    'Wants a traditional five-day, all-day in-person campus with daytime supervision',
    'Prefers a lecture-and-test classroom over applied, mastery-based learning',
    'Isn’t looking for an athletics-centered academic model',
    'Wants a fully self-paced program with no live or coached instruction',
  ];
  const essentials = [
    { icon: GraduationCap, k: 'Grades', v: '5–8 (expanding)' },
    { icon: MapPin, k: 'Where', v: 'Virtual + in-person · California student-athletes' },
    { icon: Calendar, k: 'School Year', v: '2026–27 begins August 24, 2026' },
    { icon: Users, k: 'Enrollment', v: 'Now accepting 2026–27 scholars' },
  ];
  return (
    <section className="relative py-28 px-6 lg:px-8" style={{ backgroundColor: BG }} data-testid="academy-fit">
      <div className="relative z-10 max-w-7xl mx-auto">
        <Label>Why Choose Scholar Athlete Academy</Label>
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tight mb-16">Five things that,<br/><span className="text-gray-600">together, make us different.</span></h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/[0.04] mb-24">
          {why.map((w) => (
            <div key={w.n} className="p-7" style={{ backgroundColor: SURFACE }} data-testid={`why-card-${w.n}`}>
              <div className="text-[42px] font-black leading-none mb-3" style={{ color: `${TC}22` }}>{w.n}</div>
              <div className="text-xs font-black tracking-[0.1em] uppercase mb-2" style={{ color: TC }}>{w.name}</div>
              <p className="text-gray-400 text-xs leading-[1.7]">{w.body}</p>
            </div>
          ))}
        </div>

        <Label>Is It The Right Fit?</Label>
        <h3 className="text-2xl md:text-4xl font-black tracking-tight mb-12">Built for families who want something different.</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-24">
          <div className="p-8 border-2" style={{ borderColor: `rgba(${RGB},0.4)`, backgroundColor: `rgba(${RGB},0.04)` }} data-testid="fit-good">
            <div className="text-sm font-black text-white mb-6">A great fit if your family…</div>
            <ul className="space-y-4">{fit.map((f, i) => (<li key={i} className="flex gap-3 text-sm text-gray-300 leading-relaxed"><CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: TC }} />{f}</li>))}</ul>
          </div>
          <div className="p-8 border-2 border-white/10" style={{ backgroundColor: SURFACE }} data-testid="fit-not">
            <div className="text-sm font-black text-white mb-6">May not be the best fit if your family…</div>
            <ul className="space-y-4">{notFit.map((f, i) => (<li key={i} className="flex gap-3 text-sm text-gray-500 leading-relaxed"><X className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-600" />{f}</li>))}</ul>
          </div>
        </div>

        <Label>Enrollment Essentials</Label>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.04] mb-12" data-testid="essentials">
          {essentials.map((e, i) => (
            <div key={i} className="p-7" style={{ backgroundColor: SURFACE }} data-testid={`essential-${i}`}>
              <e.icon className="w-5 h-5 mb-4" style={{ color: GOLD }} />
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-gray-600 mb-1">{e.k}</div>
              <div className="text-sm font-black text-white">{e.v}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-4">
          <button onClick={() => navigate('/academy/enroll')} className="group flex items-center gap-2 px-8 py-4 font-black text-sm uppercase tracking-widest text-black transition-transform hover:-translate-y-0.5" style={{ backgroundColor: TC }} data-testid="fit-enroll-btn">Start Enrollment <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></button>
          <button onClick={() => document.getElementById('tuition')?.scrollIntoView({ behavior: 'smooth' })} className="flex items-center gap-2 px-8 py-4 font-black text-sm uppercase tracking-widest border-2 border-white/20 text-white hover:border-white transition-colors" data-testid="fit-plans-btn">View Plans & Tuition</button>
        </div>
      </div>
    </section>
  );
};
